import Drawer from '../../Drawer';
import {
  formatBundleExplosionUnits,
  getBundleExplosionAuditGuidance,
  getBundleExplosionStatusLabel,
} from '../../../lib/bundleDemandExplosionHelpers';

function statusTone(hasException) {
  return hasException
    ? 'border-transparent bg-amber-50 text-amber-700'
    : 'border-transparent bg-emerald-50 text-emerald-700';
}

export function BundleDemandExplosionInspectDrawer({
  open,
  onClose,
  selectedRow,
  executionRows,
  marginFromSidebar,
}) {
  return (
    <Drawer
      isOpen={open}
      onClose={onClose}
      title={selectedRow ? `${selectedRow.executionSku} · Bundle lineage` : 'Bundle lineage'}
      showActionButtons={false}
      width="720px"
      marginFromSidebar={marginFromSidebar}
      bodyClassName="!p-0"
    >
      {selectedRow ? (
        <div className="flex h-full flex-col overflow-hidden">
          <p className="border-b border-gray-200 px-5 py-3 text-sm text-gray-500">
            Use this drill-through to inspect how one bundle execution SKU fans out into component demand, and
            whether any formula or mapping exceptions need cleanup before replacement demand and the final ledger.
          </p>

          <div className="grid gap-2 border-b border-gray-200 px-5 py-4 md:grid-cols-4">
            <div className="rounded-md border border-gray-200 bg-white px-3 py-2">
              <p className="text-[11px] uppercase tracking-[0.12em] text-gray-500">Execution SKU</p>
              <p className="mt-1 text-sm font-semibold text-gray-900">{selectedRow.executionSku}</p>
            </div>
            <div className="rounded-md border border-gray-200 bg-white px-3 py-2">
              <p className="text-[11px] uppercase tracking-[0.12em] text-gray-500">Execution product</p>
              <p className="mt-1 text-sm font-semibold text-gray-900">{selectedRow.executionProductName}</p>
            </div>
            <div className="rounded-md border border-gray-200 bg-white px-3 py-2">
              <p className="text-[11px] uppercase tracking-[0.12em] text-gray-500">Subcategory</p>
              <p className="mt-1 text-sm font-semibold text-gray-900">{selectedRow.executionSubCategory}</p>
            </div>
            <div className="rounded-md border border-gray-200 bg-white px-3 py-2">
              <p className="text-[11px] uppercase tracking-[0.12em] text-gray-500">Status</p>
              <p className="mt-1 text-sm font-semibold text-gray-900">
                {getBundleExplosionStatusLabel(selectedRow)}
              </p>
            </div>
          </div>

          <div className="flex flex-col flex-1 gap-4 overflow-auto px-5 py-5 lg:grid-cols-[minmax(0,0.92fr)_minmax(0,1.45fr)] lg:grid">
            <aside className="min-w-0 space-y-4">
              <section className="min-w-0 rounded-lg border border-gray-200 bg-white p-2 lg:p-4">
                <h3 className="text-sm font-semibold text-gray-900">Audit guidance</h3>
                <p className="mt-2 text-sm text-gray-600">{getBundleExplosionAuditGuidance(selectedRow)}</p>
              </section>

              <section className="min-w-0 rounded-lg border border-gray-200 bg-white p-2 lg:p-4">
                <h3 className="text-sm font-semibold text-gray-900">Selected row metrics</h3>
                <dl className="mt-3 space-y-2 text-sm">
                  <div className="flex items-center justify-between gap-4">
                    <dt className="text-gray-500">Units per bundle</dt>
                    <dd className="font-medium text-gray-900">
                      {formatBundleExplosionUnits(selectedRow.unitsPerBundle)}
                    </dd>
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <dt className="text-gray-500">Forecast units</dt>
                    <dd className="font-medium text-gray-900">
                      {formatBundleExplosionUnits(selectedRow.forecastUnits)}
                    </dd>
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <dt className="text-gray-500">Component units</dt>
                    <dd className="font-medium text-gray-900">
                      {formatBundleExplosionUnits(selectedRow.componentUnits)}
                    </dd>
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <dt className="text-gray-500">Supplier</dt>
                    <dd className="font-medium text-gray-900">{selectedRow.supplier}</dd>
                  </div>
                </dl>
              </section>
            </aside>

            <section className="rounded-lg border border-gray-200 bg-white">
              <div className="border-b border-gray-200 px-4 py-3">
                <h3 className="text-sm font-semibold text-gray-900">Execution SKU lineage</h3>
                <p className="mt-1 text-xs text-gray-500">
                  All workbook rows for {selectedRow.executionSku} are shown here so planners can audit the complete
                  bundle-to-component fan-out.
                </p>
              </div>
              <div className="max-h-[calc(100vh-2rem)] overflow-auto">
                <table className="min-w-full text-left text-sm">
                  <thead className="sticky top-0 z-10 bg-slate-50 text-xs uppercase tracking-[0.12em] text-gray-500">
                    <tr>
                      <th className="px-4 py-3 font-medium">Component SKU</th>
                      <th className="px-4 py-3 font-medium">Component product</th>
                      <th className="px-4 py-3 text-right font-medium">Units / bundle</th>
                      <th className="px-4 py-3 text-right font-medium">Component units</th>
                      <th className="px-4 py-3 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {executionRows.map(row => (
                      <tr
                        key={row.id}
                        className={`border-b border-gray-100 ${row.id === selectedRow.id ? 'bg-slate-50' : 'bg-white'}`}
                      >
                        <td className="px-4 py-3 align-top font-medium text-gray-900">{row.componentSku}</td>
                        <td className="px-4 py-3 align-top text-gray-600">{row.componentProductName}</td>
                        <td className="px-4 py-3 text-right align-top text-gray-900">
                          {formatBundleExplosionUnits(row.unitsPerBundle)}
                        </td>
                        <td className="px-4 py-3 text-right align-top font-medium text-gray-900">
                          {formatBundleExplosionUnits(row.componentUnits)}
                        </td>
                        <td className="px-4 py-3 align-top">
                          <span
                            className={`inline-flex rounded-md border px-2 py-1 text-[11px] whitespace-nowrap font-medium ${statusTone(row.hasException)}`}
                          >
                            {getBundleExplosionStatusLabel(row)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </div>
        </div>
      ) : null}
    </Drawer>
  );
}
